/**
 * @author       matthew@zumper.com (Matthew Talamini)
 * @copyright    Copyright (c) 2023, Zumper
 * @description  selectors for the cityGuideCity section of currentSearch
 */

import { transformAndToAmpersand, transformSlashesToAmpersand } from '@zcode-ui/locale-guide'
import { toTitleCase } from '@zcode/nr/es/shared/utils'
import type { CityGuideCityMetaType, State } from '@zcode/state-type'
import { numberize } from '@zcode/utils'
import { compose } from 'redux'

// importing this from index.js crashes React
const selectCurrentSearch = (state: State) => state?.currentSearch

export const selectCityGuideCity = compose(
  (currentSearch) => currentSearch?.cityGuideCity?.data,
  selectCurrentSearch
)

export const selectCityGuideBlogArticles = compose((city) => city?.articles, selectCityGuideCity)

export const selectCityGuideSatisfactionSurvey = compose(
  (city) => city?.survey,
  selectCityGuideCity
)

export const selectCityGuideContent = compose((data) => data?.content, selectCityGuideCity)
export const selectCityGuideEmployment = compose(
  (content) => content?.employment,
  selectCityGuideContent
)
// returns an array
export const selectEmploymentContentParagraphs = compose(
  (data) => data?.content,
  selectCityGuideEmployment
)

export const selectCityGuideIndustries = compose((data) => data?.industries, selectCityGuideCity)
export const selectCityGuideTopEmployers = compose(
  (data) => data?.topEmployers,
  selectCityGuideCity
)

const roundDownToNearestThousand = (n: number) => Math.floor(n / 1000) * 1000
const roundDownToNearestHundred = (n: number) => Math.floor(n / 100) * 100
const roundDownToNearestTen = (n: number) => Math.floor(n / 10) * 10
const roundDownForDisplay = (n: number) => {
  if (n > 1001) {
    return roundDownToNearestThousand(n)
  } else if (n > 101) {
    return roundDownToNearestHundred(n)
  } else {
    return roundDownToNearestTen(n)
  }
}

export const selectTopEmployersForDisplay = (state: State) => {
  const topEmployers = selectCityGuideTopEmployers(state)
  return (
    topEmployers &&
    [...topEmployers]
      .sort((a, b) => {
        const numA = roundDownForDisplay(a.count_lower)
        const numB = roundDownForDisplay(b.count_lower)
        if (numA === numB) {
          if (a.name < b.name) {
            return -1
          } else if (a.name > b.name) {
            return 1
          } else {
            return 0
          }
        } else {
          return numB - numA
        }
      })
      .slice(0, 6)
      .map((employer) => {
        return {
          title: toTitleCase(employer.name),
          subtitle: `${numberize(roundDownForDisplay(employer.count_lower))}+ employees`,
        }
      })
  )
}
export const selectHasSufficientIndustry = (state: State) => {
  const industries = selectCityGuideIndustries(state)
  // if we have at least 1 industry with more than 51 employees, we can display the table
  if (industries) {
    return Object.values(industries)?.some((a) => a > 51)
  }
}
export const selectIndustriesForDisplay = (state: State) => {
  const industries = selectCityGuideIndustries(state)
  if (industries) {
    const industriesAsArray = Object.keys(industries).map((k) => {
      return { industry: k, employed: industries[k] }
    })
    return industriesAsArray
      ?.sort((a, b) => {
        const numA = roundDownForDisplay(a.employed)
        const numB = roundDownForDisplay(b.employed)
        if (numA === numB) {
          if (a.industry < b.industry) {
            return -1
          } else if (a.industry > b.industry) {
            return 1
          } else {
            return 0
          }
        } else {
          return numB - numA
        }
      })
      ?.slice(0, 5)
      ?.map((industry) => {
        return {
          industry: transformAndToAmpersand(
            transformSlashesToAmpersand(toTitleCase(industry.industry))
          ),
          employed: `${numberize(roundDownForDisplay(industry.employed))}+`,
        }
      })
  }
  return industries
}

export const selectCityGuideCityMeta = compose(
  (currentSearch) => currentSearch?.cityGuideCity?.meta as CityGuideCityMetaType,
  selectCurrentSearch
)

export const selectCityGuideCityIsLoading = compose(
  (meta) => meta?.fetching,
  selectCityGuideCityMeta
)

export const selectCityGuideCityError = compose((meta) => meta?.error, selectCityGuideCityMeta)

export const selectCityGuideCityKey = compose((meta) => meta?.key, selectCityGuideCityMeta)

export const selectCurrentSearchCityOrHoodExists = (state: State) => {
  // indexation logic currently depends on currentSearch city & hood
  // if that changes, update this too
  const currentSearchCityExists = !!state?.currentSearch?.city
  const currentSearchHoodExists = !!state?.currentSearch?.hood
  return currentSearchCityExists || currentSearchHoodExists
}
